import React, { useState } from 'react';
import { ChatConfig, SavedConversation, LabelPromptMapping } from '../types';
import { EmailService } from '../services/emailService';
import { EmailData } from '../types';
import { systemPromptText, backendSystemPromptText, labelPromptsText } from '../prompts/PromptText';

interface AdminPanelProps {
  config: ChatConfig;
  onConfigChange: (config: ChatConfig) => void;
  onClearHistory: () => void;
  savedConversations: SavedConversation[];
  onLoadConversation: (conversation: SavedConversation | null) => void;
  onDeleteConversation: (id: string) => void;
  onUpdateConversationTitle: (id: string, newTitle: string) => void;
  onUpdateConversationSummary: (id: string) => void;
  viewingConversation: SavedConversation | null;
  onRefreshConversations: () => Promise<void>;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ 
  config, 
  onConfigChange, 
  onClearHistory,
  savedConversations,
  onLoadConversation,
  onDeleteConversation,
  onUpdateConversationTitle,
  onUpdateConversationSummary,
  viewingConversation,
  onRefreshConversations
}) => {
  const [emailData, setEmailData] = useState<EmailData>({
    to: 'anne-valentine.rensonnet@avocat.be',
    subject: '',
    content: '',
    isHtml: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [showEmailSection, setShowEmailSection] = useState(false);
  
  // State pour la gestion des labels
  const [editingLabel, setEditingLabel] = useState<LabelPromptMapping | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newPrompt, setNewPrompt] = useState('');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailData.subject.trim() || !emailData.content.trim()) {
      setResult({
        success: false,
        message: 'Veuillez remplir le sujet et le contenu'
      });
      return;
    }

    if (!EmailService.validateEmail(emailData.to)) {
      setResult({
        success: false,
        message: 'Adresse email invalide'
      });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const emailToSend = {
        ...emailData,
        content: emailData.isHtml 
          ? EmailService.generateLegalEmailTemplate(emailData.subject, emailData.content)
          : emailData.content
      };

      const result = await EmailService.sendEmail(emailToSend);
      setResult(result);

      if (result.success) {
        // Réinitialiser le formulaire en cas de succès
        setEmailData({
          to: 'anne-valentine.rensonnet@avocat.be',
          subject: '',
          content: '',
          isHtml: true
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Erreur lors de l\'envoi'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (field: keyof EmailData, value: string | boolean) => {
    setEmailData((prev: EmailData) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditTitle = (conversation: SavedConversation) => {
    setEditingTitle(conversation.id);
    setEditingTitleValue(conversation.title);
  };

  const handleSaveTitle = (id: string) => {
    if (editingTitleValue.trim()) {
      onUpdateConversationTitle(id, editingTitleValue.trim());
    }
    setEditingTitle(null);
    setEditingTitleValue('');
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setEditingTitleValue('');
  };

  // Handlers pour la gestion des labels
  const handleAddLabel = () => {
    if (newLabel.trim() && newPrompt.trim()) {
      const newLabelPrompt: LabelPromptMapping = {
        id: Date.now().toString(),
        label: newLabel.trim(),
        prompt: newPrompt.trim()
      };
      
      onConfigChange({
        ...config,
        labelPrompts: [...config.labelPrompts, newLabelPrompt]
      });
      
      setNewLabel('');
      setNewPrompt('');
    }
  };

  const handleEditLabel = (labelPrompt: LabelPromptMapping) => {
    setEditingLabel(labelPrompt);
    setNewLabel(labelPrompt.label);
    setNewPrompt(labelPrompt.prompt);
  };

  const handleSaveLabel = () => {
    if (editingLabel && newLabel.trim() && newPrompt.trim()) {
      onConfigChange({
        ...config,
        labelPrompts: config.labelPrompts.map(lp => 
          lp.id === editingLabel.id 
            ? { ...lp, label: newLabel.trim(), prompt: newPrompt.trim() }
            : lp
        )
      });
      
      setEditingLabel(null);
      setNewLabel('');
      setNewPrompt('');
    }
  };

  const handleCancelLabelEdit = () => {
    setEditingLabel(null);
    setNewLabel('');
    setNewPrompt('');
  };

  const handleDeleteLabel = (id: string) => {
    onConfigChange({
      ...config,
      labelPrompts: config.labelPrompts.filter(lp => lp.id !== id)
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="professional-bg" style={{ minHeight: '100%' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2>⚖️ Administration - JudicIA</h2>
        <p style={{ color: '#34495e', fontSize: '1.1rem', fontStyle: 'italic', marginTop: '10px' }}>
          Gestion professionnelle de la configuration et des consultations
        </p>
      </div>

      {/* Affichage de la conversation chargée (New Section) */}
      {viewingConversation && (
        <div className="config-section elegant-border" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>📖 Consultation: {viewingConversation.title}</h3>
            <button
              onClick={() => onLoadConversation(null)}
              className="btn"
              style={{
                background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                fontSize: '0.9rem',
                padding: '8px 16px',
                borderRadius: '8px'
              }}
            >
              ✕ Fermer
            </button>
          </div>
          
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto', 
            border: '1px solid #e9ecef', 
            borderRadius: '12px', 
            padding: '20px',
            background: '#f8f9fa'
          }}>
            {viewingConversation.messages.map((message, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '20px',
                  padding: '15px',
                  borderRadius: '10px',
                  background: message.role === 'user' ? '#e3f2fd' : '#f1f8e9',
                  borderLeft: `4px solid ${message.role === 'user' ? '#2196f3' : '#4caf50'}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  <strong style={{ 
                    color: message.role === 'user' ? '#2196f3' : '#4caf50',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {message.role === 'user' ? 'Client' : 'JudicIA'}
                  </strong>
                  <span>{formatTimestamp(message.timestamp!)}</span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '1rem', lineHeight: '1.6' }}>
                  {message.content}
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ 
            marginTop: '15px', 
            fontSize: '0.9rem', 
            color: '#666',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <span>Messages: {viewingConversation.messages.length}</span>
            <span>Tokens: {viewingConversation.totalTokens}</span>
            <span>Créé: {formatDate(viewingConversation.createdAt)}</span>
          </div>

          {/* Affichage du résumé et des coordonnées pour la conversation chargée */}
          {viewingConversation.summary && (
            <div style={{ 
              marginTop: '15px', 
              padding: '15px', 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
              borderRadius: '10px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '8px' }}>
                📝 Résumé: {viewingConversation.summary}
              </div>
              {viewingConversation.contactInfo && viewingConversation.contactInfo !== 'Aucune coordonnée mentionnée' && (
                <div style={{ fontSize: '0.9rem', color: '#424242' }}>
                  📞 Coordonnées: {viewingConversation.contactInfo}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Historique des conversations */}
      <div className="config-section elegant-border" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0 }}>📚 Historique des consultations</h3>
            <p style={{ color: '#666', fontSize: '0.95rem', margin: '5px 0 0 0', fontStyle: 'italic' }}>
              ✅ Toutes les conversations sont sauvegardées sur le serveur MySQL
            </p>
          </div>
          <button
            onClick={onRefreshConversations}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🔄 Actualiser
          </button>
        </div>
        
        {savedConversations.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#7f8c8d', 
            fontStyle: 'italic',
            padding: '40px 20px',
            border: '2px dashed #bdc3c7',
            borderRadius: '12px',
            fontSize: '1.1rem'
          }}>
            <div style={{ marginBottom: '15px', fontSize: '3rem' }}>📋</div>
            Aucune consultation sauvegardée
          </div>
        ) : (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {savedConversations.map((conversation) => (
              <div
                key={conversation.id}
                className="conversation-item"
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '15px',
                  background: 'white',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    {editingTitle === conversation.id ? (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={editingTitleValue}
                          onChange={(e) => setEditingTitleValue(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '2px solid #d4af37',
                            borderRadius: '6px',
                            fontSize: '1rem',
                            fontFamily: 'Georgia, serif'
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveTitle(conversation.id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveTitle(conversation.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#27ae60',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            padding: '6px 12px',
                            background: '#95a5a6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}
                        >
                          ✗
                        </button>
                      </div>
                    ) : (
                      <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: '#2c3e50' }}>
                        {conversation.title}
                      </h4>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => onLoadConversation(conversation)}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      📖 Consulter
                    </button>
                    <button
                      onClick={() => handleEditTitle(conversation)}
                      style={{
                        padding: '8px 12px',
                        background: '#f39c12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => onDeleteConversation(conversation.id)}
                      style={{
                        padding: '8px 12px',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                  <span>Messages: {conversation.messages.length}</span>
                  <span style={{ marginLeft: '20px' }}>Tokens: {conversation.totalTokens}</span>
                </div>
                
                <div style={{ fontSize: '0.8rem', color: '#999' }}>
                  Créé: {formatDate(conversation.createdAt)}
                  {conversation.lastModified.getTime() !== conversation.createdAt.getTime() && (
                    <span style={{ marginLeft: '20px' }}>
                      Modifié: {formatDate(conversation.lastModified)}
                    </span>
                  )}
                </div>

                {/* Affichage du résumé et des coordonnées */}
                {conversation.summary && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#495057', marginBottom: '6px' }}>
                      📝 {conversation.summary}
                    </div>
                    {conversation.contactInfo && conversation.contactInfo !== 'Aucune coordonnée mentionnée' && (
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        📞 {conversation.contactInfo}
                      </div>
                    )}
                  </div>
                )}

                {/* Bouton pour régénérer le résumé */}
                <div style={{ marginTop: '12px' }}>
                  <button
                    onClick={() => onUpdateConversationSummary(conversation.id)}
                    style={{
                      padding: '6px 12px',
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    🔄 Régénérer le résumé
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Configuration Norbert */}
      <div className="config-section elegant-border" style={{ marginBottom: '30px' }}>
        <h3>⚙️ Configuration de JudicIA</h3>
        <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '20px', fontStyle: 'italic' }}>
          ✅ La configuration est automatiquement sauvegardée dans votre navigateur
        </p>
        
        {/* Prompts système - Pleine largeur */}
        <div style={{ marginBottom: '30px' }}>
          <div className="input-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="systemPrompt">Prompt système - Agent Frontend</label>
            <textarea
              id="systemPrompt"
              rows={10}
              value={config.systemPrompt}
              onChange={(e) => onConfigChange({ ...config, systemPrompt: e.target.value })}
              placeholder="Instructions spéciales pour l'agent frontend (JudicIA)..."
              style={{ width: '100%', minHeight: '200px' }}
            />
          </div>

          <div className="input-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="backendSystemPrompt">Prompt système - Agent Backend</label>
            <textarea
              id="backendSystemPrompt"
              rows={10}
              value={config.backendSystemPrompt}
              onChange={(e) => onConfigChange({ ...config, backendSystemPrompt: e.target.value })}
              placeholder="Instructions spéciales pour l'agent backend..."
              style={{ width: '100%', minHeight: '200px' }}
            />
          </div>
        </div>

        {/* Gestion des Labels et Prompts Spécifiques */}
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ 
            fontSize: '1.1rem', 
            fontWeight: '600', 
            color: '#2c3e50', 
            marginBottom: '15px',
            borderBottom: '2px solid #3498db',
            paddingBottom: '8px'
          }}>
            🏷️ Labels et Prompts Spécifiques
          </h4>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '20px', fontStyle: 'italic' }}>
            Configurez les labels que l'agent backend peut utiliser et les prompts spécifiques correspondants pour l'agent frontend.
          </p>

          {/* Formulaire d'ajout/édition de label */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
            padding: '20px', 
            borderRadius: '10px',
            marginBottom: '20px',
            border: '1px solid #dee2e6'
          }}>
            <h5 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '15px', color: '#495057' }}>
              {editingLabel ? '✏️ Modifier le label' : '➕ Ajouter un nouveau label'}
            </h5>
            
            
            <div style={{ display: 'flex', gap: '10px' }}>
              {editingLabel ? (
                <>
                  <button
                    onClick={handleSaveLabel}
                    disabled={!newLabel.trim() || !newPrompt.trim()}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: (!newLabel.trim() || !newPrompt.trim()) ? 0.6 : 1
                    }}
                  >
                    💾 Sauvegarder
                  </button>
                  <button
                    onClick={handleCancelLabelEdit}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    ❌ Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={handleAddLabel}
                  disabled={!newLabel.trim() || !newPrompt.trim()}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: (!newLabel.trim() || !newPrompt.trim()) ? 0.6 : 1
                  }}
                >
                  ➕ Ajouter le label
                </button>
              )}
            </div>
          </div>

          {/* Liste des labels existants */}
          {config.labelPrompts.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h5 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '15px', color: '#495057' }}>
                📋 Labels configurés ({config.labelPrompts.length})
              </h5>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {config.labelPrompts.map((labelPrompt) => (
                  <div
                    key={labelPrompt.id}
                    style={{
                      background: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '10px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          marginBottom: '8px'
                        }}>
                          [{labelPrompt.label}]
                        </div>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#495057',
                          lineHeight: '1.4',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {labelPrompt.prompt}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                        <button
                          onClick={() => handleEditLabel(labelPrompt)}
                          style={{
                            padding: '6px 10px',
                            background: '#f39c12',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteLabel(labelPrompt.id)}
                          style={{
                            padding: '6px 10px',
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Autres paramètres - Grille */}
        <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div className="input-group">
            <label htmlFor="temperature">Température (créativité)</label>
            <input
              type="range"
              id="temperature"
              min="0"
              max="2"
              step="0.1"
              value={config.temperature}
              onChange={(e) => onConfigChange({ ...config, temperature: parseFloat(e.target.value) })}
            />
            <span style={{ fontSize: '0.9rem', color: '#666' }}>{config.temperature}</span>
          </div>

          <div className="input-group">
            <label htmlFor="maxTokens">Tokens maximum</label>
            <input
              type="number"
              id="maxTokens"
              value={config.maxTokens}
              onChange={(e) => onConfigChange({ ...config, maxTokens: parseInt(e.target.value) })}
              min="100"
              max="4000"
            />
          </div>

          <div className="input-group">
            <label htmlFor="model">Modèle</label>
            <select
              id="model"
              value={config.model}
              onChange={(e) => onConfigChange({ ...config, model: e.target.value })}
            >
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>

          <div className="input-group">
            <label htmlFor="contextBefore">Contexte avant</label>
            <textarea
              id="contextBefore"
              rows={3}
              value={config.contextBefore}
              onChange={(e) => onConfigChange({ ...config, contextBefore: e.target.value })}
              placeholder="Texte à ajouter avant le message..."
            />
          </div>

          <div className="input-group">
            <label htmlFor="contextAfter">Contexte après</label>
            <textarea
              id="contextAfter"
              rows={3}
              value={config.contextAfter}
              onChange={(e) => onConfigChange({ ...config, contextAfter: e.target.value })}
              placeholder="Texte à ajouter après le message..."
            />
          </div>
        </div>

        <div style={{ marginTop: '30px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <button onClick={onClearHistory} className="btn" style={{ 
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            fontSize: '1rem',
            padding: '12px 24px'
          }}>
            🗑️ Effacer l'historique
          </button>
          <button
            onClick={() => {
              const defaultConfig: ChatConfig = {
                systemPrompt: systemPromptText,
                backendSystemPrompt: backendSystemPromptText,
                temperature: 0.7,
                maxTokens: 2000,
                model: 'gpt-4',
                contextBefore: '',
                contextAfter: '',
                labelPrompts: labelPromptsText
              };
              onConfigChange(defaultConfig);
            }}
            className="btn"
            style={{ 
              background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
              fontSize: '1rem',
              padding: '12px 24px'
            }}
          >
            🔄 Réinitialiser
          </button>
        </div>
      </div>

      {/* Bouton pour afficher/masquer la section email */}
      <div style={{ marginBottom: '25px' }}>
        <button
          onClick={() => setShowEmailSection(!showEmailSection)}
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
            fontSize: '1.1rem',
            padding: '12px 24px',
            width: '100%',
            borderRadius: '10px'
          }}
        >
          {showEmailSection ? '📧 Masquer l\'envoi d\'email' : '📧 Afficher l\'envoi d\'email'}
        </button>
      </div>

      {/* Envoi d'Email - Section conditionnelle */}
      {showEmailSection && (
        <div className="config-section elegant-border">
          <h3>📧 Envoi d'Email professionnel</h3>
          <p style={{ color: '#666', fontSize: '1rem', marginBottom: '25px' }}>
            Envoyez des emails via votre compte Gmail avec le template professionnel JudicIA
          </p>
          
          <form onSubmit={handleEmailSubmit}>
            <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div className="input-group">
                <label htmlFor="emailTo">Destinataire</label>
                <input
                  type="email"
                  id="emailTo"
                  value={emailData.to}
                  onChange={(e) => handleEmailChange('to', e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="emailSubject">Sujet</label>
                <input
                  type="text"
                  id="emailSubject"
                  value={emailData.subject}
                  onChange={(e) => handleEmailChange('subject', e.target.value)}
                  required
                />
              </div>

              <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="emailContent">Contenu</label>
                <textarea
                  id="emailContent"
                  rows={8}
                  value={emailData.content}
                  onChange={(e) => handleEmailChange('content', e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label>
                  <input
                    type="checkbox"
                    checked={emailData.isHtml}
                    onChange={(e) => handleEmailChange('isHtml', e.target.checked)}
                  />
                  Format HTML
                </label>
              </div>
            </div>

            <div style={{ marginTop: '25px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button type="submit" className="btn" disabled={isLoading} style={{ 
                fontSize: '1.1rem',
                padding: '12px 30px',
                minWidth: '200px'
              }}>
                {isLoading ? (
                  <>
                    <span className="loading"></span>
                    Envoi en cours...
                  </>
                ) : (
                  '📧 Envoyer l\'email'
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className={`status-message ${result.success ? 'status-success' : 'status-error'}`} style={{ marginTop: '20px' }}>
              <span style={{ fontSize: '1.2rem' }}>
                {result.success ? '✅' : '❌'}
              </span>
              <strong>{result.success ? 'Succès:' : 'Erreur:'}</strong> {result.message}
            </div>
          )}
        </div>
      )}

      {/* Fonctionnalité automatique */}
      <div className="config-section elegant-border" style={{ marginTop: '25px' }}>
        <h3>🔄 Fonctionnalité automatique</h3>
        <p style={{ color: '#666', fontSize: '1rem', marginBottom: '15px' }}>
          Quand JudicIA détecte une situation nécessitant une prise de contact, 
          la consultation complète est automatiquement envoyée par email à votre adresse.
        </p>
        <div style={{ fontSize: '0.95rem', color: '#7f8c8d' }}>
          <div style={{ marginBottom: '8px' }}>
            📧 Email de destination: <strong>anne-valentine.rensonnet@avocat.be</strong>
          </div>
          <div>
            🔍 Déclencheur: <strong>[PRISE-CONTACT]</strong> dans la réponse de JudicIA
          </div>
        </div>
      </div>
    </div>
  );
}; 